import deepmerge from "deepmerge";
import {
  PAGE_TYPES,
  ID_PREFIX_SORTABLE_CONTAINER,
  MATCHING_RIGHT_OFFSET,
} from "../common/constants";
import i18n from "i18next";
import { QUESTION_PLUGINS } from "../common/scene_mandatory";
import {
  normalizeSceneMandatoryDefaults,
  repairQuestionMandatoryOnPlainScenes,
} from "../common/sceneDefaults.es6";

export function serialize(state) {
  if (state && state.present) {
    let result;
    switch (state.present.version) {
      case 2:
      case "2":
        result = multipleAnswerSerializer(state);
        break;
      default:
        result = state;
    }
    // Always normalize Matching's right column to the count-independent key
    // scheme (idempotent, no-op for non-Matching / already-migrated content).
    result = matchingContainerSerializer(result);
    const exercises = pruneNonQuestionExercises(result.present);
    if (exercises !== result.present.exercises) {
      result = { ...result, present: { ...result.present, exercises } };
    }
    // Must run after the prune.
    const viewToolbarsById = repairQuestionMandatoryOnPlainScenes(result.present);
    return viewToolbarsById === result.present.viewToolbarsById
      ? result
      : { ...result, present: { ...result.present, viewToolbarsById } };
  }
  return state;
}

/**
 * Drop exercise entries that do not belong to a question.
 *
 * Toolbar updates on non-question boxes. An entry is kept when it carries a correctAnswer
 * (every real one does, from ADD_BOX) or its box is a question plugin.
 *
 * @param {object} present state slice ({ exercises, pluginToolbarsById })
 * @returns the exercises map — the same reference when nothing was dropped
 */
export function pruneNonQuestionExercises(present) {
  const exercises = present && present.exercises;
  if (!exercises) return exercises;
  const toolbars = present.pluginToolbarsById || {};
  const isQuestion = (boxId, entry) =>
    (entry && "correctAnswer" in entry) ||
    (toolbars[boxId] && QUESTION_PLUGINS.indexOf(toolbars[boxId].pluginId) !== -1);

  let pruned = exercises;
  Object.keys(exercises).forEach((pageId) => {
    const page = exercises[pageId];
    const entries = page && page.exercises;
    if (!entries) return;
    const stale = Object.keys(entries).filter((boxId) => !isQuestion(boxId, entries[boxId]));
    if (!stale.length) return;
    const kept = { ...entries };
    stale.forEach((boxId) => delete kept[boxId]);
    if (pruned === exercises) pruned = { ...exercises };
    pruned[pageId] = { ...page, exercises: kept };
  });
  return pruned;
}

/**
 * Re-key the Matching plugin's right (match-target) column.
 *
 * Legacy Matching render keyed the right column "Answer<i + nBoxes>", so every
 * existing container's key depended on the answer count. Changing the count
 * therefore re-mapped every right cell onto a different key, scrambling content
 * and spawning empty cells. The render now keys the right column
 * "Answer<i + MATCHING_RIGHT_OFFSET>" (count-independent); this migration moves
 * any legacy-keyed Matching content onto the new scheme so it keeps rendering.
 *
 * Touches all four places a container key is referenced: the box's
 * sortableContainers map (+ each container's own `key`), the box's ordered
 * `children` array, each child box's `container` field, and the toolbar's
 * `__pluginContainerIds`. Idempotent and scoped to Matching boxes.
 */
function matchingContainerSerializer(state) {
  if (!state || !state.present) return state;
  const present = state.present;
  const pt = present.pluginToolbarsById;
  const boxes = present.boxesById;
  if (!pt || !boxes) return state;

  const ans = (n) => ID_PREFIX_SORTABLE_CONTAINER + "Answer" + n;
  const work = [];
  for (const boxId of Object.keys(pt)) {
    const tb = pt[boxId];
    if (!tb || tb.pluginId !== "Matching") continue;
    const nBoxes = tb.state && tb.state.nBoxes;
    const box = boxes[boxId];
    if (!nBoxes || !box || !box.sortableContainers) continue;
    // Idempotent: the offset namespace only exists once migrated.
    if (box.sortableContainers[ans(MATCHING_RIGHT_OFFSET)]) continue;
    const rename = {};
    for (let j = 0; j < nBoxes; j++) {
      const oldKey = ans(nBoxes + j);
      if (box.sortableContainers[oldKey]) rename[oldKey] = ans(MATCHING_RIGHT_OFFSET + j);
    }
    if (Object.keys(rename).length) work.push({ boxId, rename });
  }
  if (!work.length) return state;

  const newBoxes = { ...boxes };
  const newPt = { ...pt };
  for (const { boxId, rename } of work) {
    const box = JSON.parse(JSON.stringify(boxes[boxId]));
    const newSC = {};
    for (const key of Object.keys(box.sortableContainers)) {
      const target = rename[key] || key;
      const container = box.sortableContainers[key];
      container.key = target;
      newSC[target] = container;
      (container.children || []).forEach((childId) => {
        if (newBoxes[childId]) {
          newBoxes[childId] = { ...newBoxes[childId], container: target };
        }
      });
    }
    box.sortableContainers = newSC;
    box.children = (box.children || []).map((k) => rename[k] || k);
    newBoxes[boxId] = box;

    const tb = pt[boxId];
    if (tb.state && tb.state.__pluginContainerIds) {
      const newPcid = {};
      for (const key of Object.keys(tb.state.__pluginContainerIds)) {
        const target = rename[key] || key;
        newPcid[target] = { ...tb.state.__pluginContainerIds[key], id: target };
      }
      newPt[boxId] = { ...tb, state: { ...tb.state, __pluginContainerIds: newPcid } };
    }
  }

  return { ...state, present: { ...present, boxesById: newBoxes, pluginToolbarsById: newPt } };
}

/**
 * Function to make sure navView state is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function navView(state) {
  let navItemDefault = {
    isExpanded: true,
    children: [],
    boxes: [],
    customSize: 0,
    linkedBoxes: {},
    level: 1,
    type: "document",
    unitNumber: 1,
    hidden: false,
    extraFiles: {},
  };

  let newState = {};
  state.forEach((element) => {
    newState.push(deepmerge(navItemDefault, element));
  });
  return newState;
}

/**
 * Function to make sure boxes state is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function boxes(state) {
  let boxesDefault = {
    level: level,
    col: 0,
    row: 0,
    position: {
      type: "relative",
      x: 0,
      y: 0,
    },
    // width: width,
    // height: height,
    content: "",
    draggable: true,
    resizable: true,
    showTextEditor: false,
    fragment: {},
    children: [],
    sortableContainers: {},
    containedViews: [],
  };

  let newState = {};
  state.forEach((element) => {
    newState.push(deepmerge(boxesDefault, element));
  });
  return newState;
}

/**
 * Function to make sure containedView is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function containedView(state) {
  let containedViewsDefault = {
    name: i18n.t("contained_view"),
    boxes: [],
    info: "new",
    type: PAGE_TYPES.SLIDE,
    extraFiles: {},
  };

  let newState = {};
  state.forEach((element) => {
    newState.push(deepmerge(containedViewsDefault, element));
  });
  return newState;
}

/**
 * Function to make sure globalConfig is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function globalConfig(state) {
  let newState = { ...state };

  if (typeof newState.canvasRatio === "string") {
    newState.canvasRatio = parseFloat(newState.canvasRatio);
  }
  if (typeof newState.canvasRatio !== "number" || !isFinite(newState.canvasRatio)) {
    newState.canvasRatio = 16 / 9;
  }

  // ratio: leave undefined when absent — UI handles "no preset highlight".
  // Do NOT auto-derive; spec requires canvasRatio to remain source-of-truth.

  newState.defaultSceneMandatory = normalizeSceneMandatoryDefaults(newState.defaultSceneMandatory);
  if (typeof newState.defaultSceneDuration !== "number") {
    newState.defaultSceneDuration = 0;
  }

  if (newState.age) {
    if (typeof newState.age.min === "string") {
      newState.age.min = parseInt(newState.age.min, 10);
    }
    if (typeof newState.age.max === "string") {
      newState.age.max = parseInt(newState.age.max, 10);
    }
  }
  newState = {
    ...{
      allowComments: true,
      allowClone: true,
      allowDownload: true,
      jobWidgetVisible: true,
      chatbotOptIn: false,
      subtitlesOnByDefault: false,
    },
    ...newState,
  };

  return newState;
}

/**
 * Function to make sure toolbarPlugin is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function pluginToolbars(state) {
  let toolbarPluginDefault = {
    showTextEditor: false,
    state: {},
    structure: {},
    style: {},
  };
  let newState = {};
  state.forEach((element) => {
    newState.push(deepmerge(toolbarPluginDefault, element));
  });

  return newState;
}

export function marksSerializer(state, version) {
  let newState = { ...state };

  if (version === "1") {
    [...state].forEach((element) => {
      let regex = /(^\d+(?:\.\d*)?%$)/g;
      let match = regex.exec(element.value);
      if (match && match.length === 2) {
        newState[element.id].value = "0:00";
      }
    });
  }

  return newState;
}

/**
 * Function to make sure toolbarView is properly built
 * @param recieved state
 * @returns fixed state with new fields that didn't exist before
 */
export function viewToolbars(state) {
  let toolbarViewDefault = {
    aspectRatio: false,
    background: "#ffffff",
    backgroundAttr: "",
    customMode: undefined,
    breacrumb: "reduced",
    courseTitle: "hidden",
    documentSubtitle: "hidden",
    documentSubitleContent: i18n.t("subtitle"),
    documentTitle: "expanded",
    documentTitleContent: i18n.t("title"),
    numPage: "hidden",
    numPageContent: "",
    viewName: "Page",
    autoNext: false,
    autoNextShowCta: false,
    sceneEndCta: false,
    minTime: 3,
    mandatoryType: "waitForTime",
    isMandatory: false,
  };

  let newState = {};
  state.forEach((element) => {
    newState.push(deepmerge(toolbarViewDefault, element));
  });
  return newState;
}

function multipleAnswerSerializer(state) {
  let boxesById = {};
  let pluginToolbarsById = {};
  let replaceAnswers = (inputDictionary, fieldToReplace) => {
    let outputDictionary = {};
    Object.keys(inputDictionary).map((scKey) => {
      if (scKey.includes("Answer")) {
        let ansN = parseInt(scKey.charAt(scKey.length - 1), 10);
        let newKey = (scKey.slice(0, -1) + (ansN - 1)).toString();
        outputDictionary[newKey] = JSON.parse(
          JSON.stringify({
            ...inputDictionary[scKey],
            [fieldToReplace]: newKey,
          })
        );
        return;
      }
      outputDictionary[scKey] = JSON.parse(JSON.stringify(inputDictionary[scKey]));
      return;
    });
    return outputDictionary;
  };
  Object.keys(state.present.boxesById).map((boxId) => {
    let currentBox = state.present.boxesById[boxId];
    let parent = currentBox.parent.toString();
    boxesById[boxId] = JSON.parse(JSON.stringify(currentBox));
    // Parent is page and box is exercise
    if (state.present.exercises[parent] && state.present.exercises[parent].exercises[boxId]) {
      if (state.present.exercises[parent].exercises[boxId].name === "MultipleAnswer") {
        boxesById[boxId] = JSON.parse(
          JSON.stringify({
            ...state.present.boxesById[boxId],
            children: state.present.boxesById[boxId].children.map((child, index) => {
              if (child.includes("Answer")) {
                return (child.slice(0, -1) + (index - 1)).toString();
              }
              return child;
            }),
            sortableContainers: replaceAnswers(
              state.present.boxesById[boxId].sortableContainers,
              "key"
            ),
          })
        );
        return;
      }
    }
    let boxPage = state.present.boxesById[parent];
    if (boxPage) {
      let page = boxPage.parent;
      if (
        state.present.exercises[page] &&
        state.present.exercises[page].exercises[parent] &&
        state.present.exercises[page].exercises[parent].name === "MultipleAnswer"
      ) {
        let container = state.present.boxesById[boxId].container.toString();
        let ansN = parseInt(container.charAt(container.length - 1), 10);
        boxesById[boxId] = JSON.parse(
          JSON.stringify({
            ...state.present.boxesById[boxId],
            container: container.includes("Answer")
              ? "sc-Answer" + (ansN - 1).toString()
              : state.present.boxesById[boxId].container.toString(),
          })
        );
        return;
      }
    }
    return;
  });
  Object.keys(state.present.pluginToolbarsById).map((boxId) => {
    if (
      state.present.pluginToolbarsById[boxId] &&
      state.present.pluginToolbarsById[boxId].pluginId === "MultipleAnswer"
    ) {
      pluginToolbarsById[boxId] = JSON.parse(
        JSON.stringify({
          ...state.present.pluginToolbarsById[boxId],
          state: {
            ...state.present.pluginToolbarsById[boxId].state,
            __pluginContainerIds: replaceAnswers(
              state.present.pluginToolbarsById[boxId].state.__pluginContainerIds,
              "id"
            ),
          },
        })
      );
      return;
    }
    pluginToolbarsById[boxId] = JSON.parse(JSON.stringify(state.present.pluginToolbarsById[boxId]));
    return;
  });
  return {
    ...state,
    present: {
      version: 3,
      ...state.present,
      boxesById,
      pluginToolbarsById,
    },
  };
}
